import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';

/**
 * Phase 5C — Mortgage-REIT levered-book risk dashboard. Consolidates a portfolio's
 * MBS assets + repo financing + rate hedges into net duration, economic leverage,
 * net interest spread and rate-shock NAV sensitivity. Reads
 * /api/credit/rmbs/reit-book/{portfolioId}/risk.
 */

const Wrapper = styled.div`padding: 22px; color: #e2e8f0;`;
const Header = styled.div`display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; margin-bottom: 18px; flex-wrap: wrap;`;
const Title = styled.h2`margin: 0; display: flex; align-items: center; gap: 8px;`;
const Sub = styled.div`color: #94a3b8; margin-top: 4px; font-size: 13px;`;

const Controls = styled.div`display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap;`;
const Field = styled.label`display: flex; flex-direction: column; gap: 5px; font-size: 11px; color: #94a3b8; text-transform: uppercase; letter-spacing: .03em;
  select, input { background: rgba(15,23,42,.8); border: 1px solid rgba(148,163,184,.25); border-radius: 7px; padding: 7px 9px; color: #e2e8f0; font-size: 13px; }
  input { width: 96px; }
`;

const Kpis = styled.div`display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 20px;`;
const Kpi = styled.div<{ $accent?: string }>`
  background: rgba(15, 23, 42, 0.55); border: 1px solid rgba(148,163,184,.14);
  border-left: 3px solid ${p => p.$accent || '#38bdf8'}; border-radius: 10px; padding: 14px 16px;
  .label { color: #94a3b8; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
  .value { font-size: 22px; font-weight: 700; color: #f8fafc; margin-top: 6px; }
  .sub { color: #64748b; font-size: 11px; margin-top: 3px; }
`;

const SectionTitle = styled.h3`font-size: 14px; color: #cbd5e1; margin: 22px 0 10px; text-transform: uppercase; letter-spacing: .05em;`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; background: rgba(15, 23, 42, 0.45); border: 1px solid rgba(148,163,184,.12);
  th, td { text-align: left; padding: 10px 12px; border-bottom: 1px solid rgba(148,163,184,.1); font-size: 13px; }
  th { color: #94a3b8; font-size: 11px; text-transform: uppercase; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
`;
const Tag = styled.span<{ $tone?: string }>`
  display: inline-block; padding: 1px 7px; border-radius: 999px; font-size: 10px; font-weight: 700;
  background: ${p => p.$tone === 'engine' ? 'rgba(34,197,94,.16)' : 'rgba(251,191,36,.16)'};
  color: ${p => p.$tone === 'engine' ? '#86efac' : '#fcd34d'};
`;
const Note = styled.div`font-size: 11px; color: #64748b; margin-top: 8px;`;

const money = (v: any) => v == null ? '--' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 2 }).format(Number(v));
const pct = (v: any, dp = 2) => v == null ? '--' : `${Number(v).toFixed(dp)}%`;
const numfmt = (v: any, dp = 2) => v == null ? '--' : Number(v).toFixed(dp);

const MortgageReitDashboard: React.FC<{ embedded?: boolean }> = () => {
  const [portfolios, setPortfolios] = useState<any[]>([]);
  const [portfolioId, setPortfolioId] = useState<string>('');
  const [bookYield, setBookYield] = useState('5.5');
  const [marketRate, setMarketRate] = useState('6.5');
  const [risk, setRisk] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/api/dimensions/portfolios').then(res => {
      const list = res.data?.content || res.data || [];
      setPortfolios(Array.isArray(list) ? list : []);
      if (Array.isArray(list) && list.length && !portfolioId) setPortfolioId(String(list[0].id));
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRisk = useCallback(async () => {
    if (!portfolioId) return;
    setLoading(true); setError('');
    try {
      const res = await axios.get(`/api/credit/rmbs/reit-book/${portfolioId}/risk`, {
        params: { assetBookYieldPct: bookYield || undefined, marketRatePct: marketRate || undefined },
      });
      setRisk(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load REIT risk');
      setRisk(null);
    } finally { setLoading(false); }
  }, [portfolioId, bookYield, marketRate]);

  useEffect(() => { if (portfolioId) loadRisk(); }, [portfolioId]); // eslint-disable-line react-hooks/exhaustive-deps

  const netDur = risk ? Number(risk.netDurationAfterHedges) : 0;

  return (
    <Wrapper>
      <Header>
        <div>
          <Title>🏦 Mortgage-REIT — Levered Book Risk</Title>
          <Sub>MBS assets + repo financing + rate hedges → net duration, economic leverage, net spread and rate-shock NAV.</Sub>
        </div>
        <Controls>
          <Field>Book (Portfolio)
            <select value={portfolioId} onChange={e => setPortfolioId(e.target.value)}>
              {portfolios.map((p: any) => <option key={p.id} value={p.id}>{p.portfolioName}</option>)}
            </select>
          </Field>
          <Field>Asset Yield (%)
            <input type="number" step="0.1" value={bookYield} onChange={e => setBookYield(e.target.value)} />
          </Field>
          <Field>Market Rate (%)
            <input type="number" step="0.25" value={marketRate} onChange={e => setMarketRate(e.target.value)} />
          </Field>
          <button onClick={loadRisk} style={{ border: '1px solid rgba(56,189,248,.4)', background: 'rgba(14,116,144,.22)', color: '#bae6fd', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Recompute</button>
        </Controls>
      </Header>

      {error && <div style={{ color: '#fca5a5', marginBottom: 12 }}>{error}</div>}
      {loading ? <div>Computing book risk…</div> : !risk ? <div style={{ color: '#64748b' }}>Select a book.</div> : (
        <>
          <Kpis>
            <Kpi $accent="#a78bfa">
              <div className="label">Asset MV</div><div className="value">{money(risk.assetMarketValue)}</div>
              <div className="sub">{risk.mbsAssetCount} MBS assets</div>
            </Kpi>
            <Kpi $accent="#38bdf8">
              <div className="label">Economic Leverage</div><div className="value">{numfmt(risk.economicLeverage, 2)}×</div>
              <div className="sub">equity {money(risk.equity)}</div>
            </Kpi>
            <Kpi $accent={netDur < 0 ? '#34d399' : '#fbbf24'}>
              <div className="label">Net Duration (post-hedge)</div>
              <div className="value" style={{ color: netDur < 0 ? '#34d399' : undefined }}>{numfmt(risk.netDurationAfterHedges, 2)}</div>
              <div className="sub">asset {numfmt(risk.assetDuration, 2)} − hedge {numfmt(risk.hedgeDurationContribution, 2)}</div>
            </Kpi>
            <Kpi $accent="#22d3ee">
              <div className="label">Repo Cost</div><div className="value">{pct(risk.repoCostPct)}</div>
              <div className="sub">borrowing {money(risk.repoBorrowing)}</div>
            </Kpi>
            <Kpi $accent="#34d399">
              <div className="label">Levered Net Spread</div>
              <div className="value">{risk.leveredNetSpreadPct != null ? pct(risk.leveredNetSpreadPct) : '--'}</div>
              <div className="sub">{risk.netInterestSpreadPct != null ? `${pct(risk.netInterestSpreadPct)} unlevered` : 'set asset yield'}</div>
            </Kpi>
          </Kpis>

          <SectionTitle>Rate-Shock NAV Sensitivity</SectionTitle>
          <Table>
            <thead><tr><th className="num">Rate Shock</th><th className="num">Asset MV Δ</th><th className="num">NAV Δ (on equity)</th></tr></thead>
            <tbody>
              {(risk.rateShockNav || []).map((r: any, i: number) => (
                <tr key={i}>
                  <td className="num">{r.rateShockBps > 0 ? '+' : ''}{r.rateShockBps} bps</td>
                  <td className="num" style={{ color: Number(r.assetMvChange) < 0 ? '#f87171' : '#34d399' }}>{money(r.assetMvChange)}</td>
                  <td className="num" style={{ color: Number(r.navChangePct) < 0 ? '#f87171' : '#34d399' }}>{pct(r.navChangePct)}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          <SectionTitle>Asset Composition</SectionTitle>
          <Table>
            <thead><tr><th>Security</th><th>Class</th><th className="num">Market Value</th><th className="num">Eff. Duration</th><th>Source</th></tr></thead>
            <tbody>
              {(risk.assets || []).length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#64748b' }}>No MBS assets in this book.</td></tr>}
              {(risk.assets || []).map((a: any, i: number) => (
                <tr key={i}>
                  <td>{a.securityName}</td>
                  <td>{a.assetClass}</td>
                  <td className="num">{money(a.marketValue)}</td>
                  <td className="num" style={{ color: Number(a.effectiveDuration) < 0 ? '#34d399' : undefined }}>{numfmt(a.effectiveDuration, 2)}</td>
                  <td><Tag $tone={a.durationSource === 'estimate' ? 'estimate' : 'engine'}>{a.durationSource}</Tag></td>
                </tr>
              ))}
            </tbody>
          </Table>

          {(risk.hedgeCount > 0) && (
            <SectionTitle>Hedges</SectionTitle>
          )}
          {(risk.hedgeCount > 0) && (
            <Table>
              <thead><tr><th className="num">Hedge Count</th><th className="num">Notional</th><th className="num">Est. DV01</th><th className="num">Duration Offset</th></tr></thead>
              <tbody>
                <tr>
                  <td className="num">{risk.hedgeCount}</td>
                  <td className="num">{money(risk.hedgeNotional)}</td>
                  <td className="num">{money(risk.hedgeDv01)}</td>
                  <td className="num">{numfmt(risk.hedgeDurationContribution, 2)}</td>
                </tr>
              </tbody>
            </Table>
          )}

          {risk.hedgeDv01Note && <Note>{risk.hedgeDv01Note}</Note>}
          {risk.durationNote && <Note>{risk.durationNote}</Note>}
          {risk.netInterestSpreadNote && <Note>{risk.netInterestSpreadNote}</Note>}
        </>
      )}
    </Wrapper>
  );
};

export default MortgageReitDashboard;
